<template>
  <div class="do">
    <!-- Print book alert -->
    <div class="do__alert">
      <svg
        aria-hidden="true"
        class="do__alert-icon"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line
          x1="12"
          y1="9"
          x2="12"
          y2="13"
        />
        <line
          x1="12"
          y1="17"
          x2="12.01"
          y2="17"
        />
      </svg>
      <p class="do__alert-text">
        {{ $t('checkout.delivery.printBookOrder') }}
      </p>
    </div>

    <h3 class="do__heading">
      {{ $t('checkout.delivery.shippingDetails') }}
    </h3>

    <!-- ═══ Delivery option cards ═══ -->
    <div
      class="do__options"
      role="radiogroup"
      :aria-label="$t('checkout.delivery.shippingDetails')"
    >
      <!-- 1. Deliver to address -->
      <div
        :class="['do__opt', { 'do__opt--on': deliveryOption === deliveryOptions.deliverToAddress }]"
        role="radio"
        :aria-checked="deliveryOption === deliveryOptions.deliverToAddress"
        tabindex="0"
        @click="deliveryOption = deliveryOptions.deliverToAddress"
        @keydown.enter="deliveryOption = deliveryOptions.deliverToAddress"
        @keydown.space.prevent="deliveryOption = deliveryOptions.deliverToAddress"
      >
        <div class="do__opt-icon">
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle
              cx="12"
              cy="10"
              r="3"
            />
          </svg>
        </div>
        <div class="do__opt-text">
          <span class="do__opt-label">{{ $t('checkout.delivery.deliveryMethods.delivery') }}</span>
          <span class="do__opt-price">{{ $t('checkout.delivery.deliveryFee') }}</span>
        </div>
        <div class="do__opt-radio">
          <div class="do__radio-dot" />
        </div>
      </div>

      <!-- 2. Deliver to school -->
      <div
        :class="['do__opt', { 'do__opt--on': deliveryOption === deliveryOptions.deliverToSchool }]"
        role="radio"
        :aria-checked="deliveryOption === deliveryOptions.deliverToSchool"
        tabindex="0"
        @click="deliveryOption = deliveryOptions.deliverToSchool"
        @keydown.enter="deliveryOption = deliveryOptions.deliverToSchool"
        @keydown.space.prevent="deliveryOption = deliveryOptions.deliverToSchool"
      >
        <div class="do__opt-icon">
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
            <path d="M6 12v5c0 1.1 2.7 2 6 2s6-.9 6-2v-5" />
          </svg>
        </div>
        <div class="do__opt-text">
          <span class="do__opt-label">{{ $t('checkout.delivery.deliveryMethods.school') }}</span>
          <span class="do__opt-badge">{{ $t('shop.free') }}</span>
        </div>
        <div class="do__opt-radio">
          <div class="do__radio-dot" />
        </div>
      </div>

      <!-- 3. Pick up from office -->
      <div
        :class="['do__opt', { 'do__opt--on': deliveryOption === deliveryOptions.collectFromOffice }]"
        role="radio"
        :aria-checked="deliveryOption === deliveryOptions.collectFromOffice"
        tabindex="0"
        @click="deliveryOption = deliveryOptions.collectFromOffice"
        @keydown.enter="deliveryOption = deliveryOptions.collectFromOffice"
        @keydown.space.prevent="deliveryOption = deliveryOptions.collectFromOffice"
      >
        <div class="do__opt-icon">
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="4"
              y="2"
              width="16"
              height="20"
              rx="2"
            />
            <path d="M9 22V12h6v10" />
            <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01" />
          </svg>
        </div>
        <div class="do__opt-text">
          <span class="do__opt-label">{{ $t('checkout.delivery.deliveryMethods.pickup') }}</span>
          <span class="do__opt-badge">{{ $t('shop.free') }}</span>
        </div>
        <div class="do__opt-radio">
          <div class="do__radio-dot" />
        </div>
      </div>
    </div>

    <!-- Note below selected option -->
    <transition
      name="do-note"
      mode="out-in"
    >
      <p
        :key="deliveryOption"
        class="do__note"
      >
        <svg
          aria-hidden="true"
          class="do__note-icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <line
            x1="12"
            y1="16"
            x2="12"
            y2="12"
          />
          <line
            x1="12"
            y1="8"
            x2="12.01"
            y2="8"
          />
        </svg>
        <span v-if="deliveryOption === deliveryOptions.deliverToAddress">
          {{ $t('checkout.delivery.deliverToAddress') }}
        </span>
        <span v-else-if="deliveryOption === deliveryOptions.deliverToSchool">
          {{ $t('checkout.delivery.deliverToSchool') }}
        </span>
        <span v-else>
          {{ $t('checkout.delivery.collectFromOffice') }}
        </span>
      </p>
    </transition>

    <!-- Expected delivery (not shown for office pickup) -->
    <p
      v-if="deliveryOption !== deliveryOptions.collectFromOffice"
      class="do__expected"
    >
      {{ $t('checkout.delivery.expected') }}
    </p>

    <!-- Delivery form (address / school / pickup) -->
    <transition
      name="do-form"
      mode="out-in"
    >
      <div :key="deliveryOption">
        <address-fields
          v-if="deliveryOption === deliveryOptions.deliverToAddress"
          :fields-required="true"
          @updateShippingDetails="setShippingInfo"
        />
        <deliver-to-school
          v-else-if="deliveryOption === deliveryOptions.deliverToSchool"
          @updateSchoolData="setSchoolInfo"
        />
        <pickup-from-office
          v-else-if="deliveryOption === deliveryOptions.collectFromOffice"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import DeliverToSchool from "@/views/checkout/print/DeliverToSchool";
import PickupFromOffice from "@/views/checkout/print/PickupFromOffice";
import AddressFields from "@/views/checkout/print/AddressFields.vue";

const DELIVERY_OPTIONS = {
  deliverToAddress: 'deliverToAddress',
  deliverToSchool: 'deliverToSchool',
  collectFromOffice: 'collectFromOffice',
}

export default {
  name: "DeliveryOptions",
  components: {
    DeliverToSchool,
    PickupFromOffice,
    AddressFields,
  },
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      deliveryOption: DELIVERY_OPTIONS.deliverToAddress,
      shippingInfo: {
        placeId: null,
        addressDetails: null,
        altPhoneNumber: null,
      },
      schoolInfo: {
        schoolId: null,
        studentName: null,
        studentClass: null,
      },
    }
  },
  computed: {
    deliveryOptions() {
      return DELIVERY_OPTIONS;
    },
    isPickupOrder() {
      return this.deliveryOption === this.deliveryOptions.collectFromOffice;
    },
    validPlaceId() {
      if (this.deliveryOption === this.deliveryOptions.deliverToAddress) {
        return !!this.shippingInfo.placeId;
      }
      else {
        return true;
      }
    }
  },
  watch: {
    deliveryOption() {
      this.shippingInfo = {
        placeId: null,
        addressDetails: null,
        altPhoneNumber: null,
      };
      this.schoolInfo = {
        schoolId: null,
        studentName: null,
        studentClass: null,
      };
      this.$emit('updateDeliveryOption', this.deliveryOption);
    },
    shippingInfo() {
      this.$emit('updateShippingInfo', this.shippingInfo);
    },
    schoolInfo() {
      this.$emit('updateSchoolInfo', this.schoolInfo);
    },
    isPickupOrder() {
      this.$emit('updateIsPickupOrder', this.isPickupOrder);
    },
    validPlaceId() {
      this.$emit('updateIsValid', this.validPlaceId);
    }
  },
  mounted() {
    this.$emit('updateDeliveryOption', this.deliveryOption);
  },
  methods: {
    setShippingInfo(data) {
      this.shippingInfo = data;
    },
    setSchoolInfo(data) {
      this.schoolInfo = data;
    },
  }
}
</script>

<style scoped lang="scss">
/* ═══════════════════════════════════════════════════
   DeliveryOptions — Uber Eats style option cards
   ═══════════════════════════════════════════════════ */

.do {
  font-family: $book-font-body;
}

/* ── Alert banner ── */
.do__alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: rgba($book-warning, 0.15);
  border: 1px solid rgba($book-warning, 0.6);
  border-radius: 12px;
  margin-bottom: 20px;
}

.do__alert-icon {
  color: darken($book-warning, 35%);
  flex-shrink: 0;
  margin-top: 1px;
}

.do__alert-text {
  font-size: 0.8125rem;
  font-weight: 500;
  color: darken($book-warning, 45%);
  line-height: 1.45;
  margin: 0;
}

/* ── Heading ── */
.do__heading {
  font-size: 1.125rem;
  font-weight: 800;
  color: $book-ink;
  margin: 0 0 14px;
  letter-spacing: -0.01em;
}

/* ═══ Option cards ═══ */
.do__options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.do__opt {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border: 2px solid $book-grey-lighter;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  animation: do-opt-in 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.do__opt:nth-child(1) { animation-delay: 0ms; }
.do__opt:nth-child(2) { animation-delay: 40ms; }
.do__opt:nth-child(3) { animation-delay: 80ms; }

@keyframes do-opt-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.do__opt:hover:not(.do__opt--on) {
  border-color: $book-grey-light;
}

.do__opt--on {
  border-color: $book-ink;
  box-shadow: 0 0 0 1px $book-ink;
}

.do__opt:focus-visible {
  outline: 2px solid $book-ink;
  outline-offset: 2px;
}

/* ── Icon ── */
.do__opt-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: $book-grey-lightest;
  color: $book-grey;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}

.do__opt--on .do__opt-icon {
  background: rgba($book-ink, 0.06);
  color: $book-ink;
}

/* ── Text ── */
.do__opt-text {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: nowrap;
}

.do__opt-label {
  font-size: 0.9375rem;
  font-weight: 700;
  color: $book-ink;
}

.do__opt-price {
  font-size: 0.75rem;
  font-weight: 700;
  color: $book-grey;
  background: $book-grey-lightest;
  padding: 2px 8px;
  border-radius: 500px;
}

.do__opt-badge {
  font-size: 0.6875rem;
  font-weight: 800;
  color: $book-green;
  background: rgba($book-green, 0.08);
  padding: 2px 8px;
  border-radius: 500px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* ── Custom radio ── */
.do__opt-radio {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid $book-grey-light;
  flex-shrink: 0;
  transition: border-color 0.15s ease;
}

.do__opt--on .do__opt-radio {
  border-color: $book-ink;
}

.do__radio-dot {
  width: 0;
  height: 0;
  border-radius: 50%;
  background: $book-ink;
  transition: width 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
              height 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.do__opt--on .do__radio-dot {
  width: 12px;
  height: 12px;
}

/* ── Note (full description) ── */
.do__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: $book-grey;
  line-height: 1.45;
  margin: 12px 0 0;
  padding: 0 4px;
}

.do__note-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: $book-grey-light;
}

/* Note transition */
.do-note-enter-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.do-note-leave-active {
  transition: opacity 0.12s ease;
}

.do-note-enter {
  opacity: 0;
  transform: translateY(4px);
}

.do-note-leave-to {
  opacity: 0;
}

/* ── Expected delivery ── */
.do__expected {
  font-size: 0.75rem;
  font-weight: 600;
  color: $book-grey-dark;
  margin: 8px 0 0;
  padding: 0 4px;
}

/* ── Form transition ── */
.do-form-enter-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.do-form-leave-active {
  transition: opacity 0.15s ease;
}

.do-form-enter {
  opacity: 0;
  transform: translateY(8px);
}

.do-form-leave-to {
  opacity: 0;
}

/* ═══ Responsive ═══ */
@media (max-width: 599px) {
  .do__opt {
    padding: 14px 14px;
    gap: 10px;
  }

  .do__opt-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  .do__opt-icon svg {
    width: 18px;
    height: 18px;
  }

  .do__opt-label {
    font-size: 0.8125rem;
  }

  .do__heading {
    font-size: 1rem;
  }
}
</style>
